import React, { useState, useEffect } from "react";
import { Edit, Delete, Settings, Download, X, Search, Filter, User, Calendar, Mail, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import EditStudent from "./EditStudent";

export default function SubStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionMenu, setActionMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const userJson = localStorage.getItem("user");
  const userId = userJson ? JSON.parse(userJson).id : null;

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/students/${userId}`,
          {
            headers: {
              "Content-Type": "application/json", 
              Authorization: `Bearer ${localStorage.getItem("token")}`, // Attach token if needed
            },
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setStudents(data.map(item => ({
          id: item.student_id,
          name: item.full_name || "N/A",
          email: item.email || "N/A",
          studentId: item.student_id || "N/A",
          status: item.status,
          createdDate: item.created_at || "N/A",
        })));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchStudents();
  }, [userId]);

  const handleDelete = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/catalog/students/${studentToDelete}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!res.ok) throw new Error("Failed to delete student.");
      setStudents(prev => prev.filter(s => s.id !== studentToDelete));
      setActionMenu(null);
      setShowConfirm(false);
      setStudentToDelete(null);
    } catch (err) {
      console.error(err);
      alert("Error deleting student.");
    }
  };

  const buildSheetData = (list) =>
    list.map((s) => ({
      "Student ID": s.studentId || "N/A",
      "Full Name": s.name || "N/A",
      Email: s.email || "N/A",
      Status: s.status || "N/A",
      "Created Date": s.createdDate || "N/A",
    }));

  const exportToExcel = (exportAll) => {
    const dataToExport = exportAll ? students : filteredStudents;
    const ws = XLSX.utils.json_to_sheet(buildSheetData(dataToExport));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    const label = exportAll ? "All" : "Filtered";
    XLSX.writeFile(
      wb,
      `Students_${label}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    setShowExportModal(false);
  };

  const filteredStudents = students.filter((s) => {
    const searchLower = filter.toLowerCase();
    const matchesSearch =
      (s.name || "").toLowerCase().includes(searchLower) ||
      (s.email || "").toLowerCase().includes(searchLower) ||
      (s.studentId || "").toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === "all" || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusOptions = [
    "all",
    ...new Set(students.map((s) => s.status).filter(Boolean)),
  ];

  const getStatusColor = (status) => {
    const colors = {
      "Incomplete Application":
        "bg-yellow-100 text-yellow-800 border-yellow-200",
      "Complete Application": "bg-green-100 text-green-800 border-green-200",
      "Conditional Offer": "bg-blue-100 text-blue-800 border-blue-200",
      Rejected: "bg-red-100 text-red-800 border-red-200",
      Defer: "bg-purple-100 text-purple-800 border-purple-200",
      "Unconditional Offer":
        "bg-emerald-100 text-emerald-800 border-emerald-200",
      Enroll: "bg-blue-100 text-blue-800 border-blue-200",
      Submitted: "bg-blue-100 text-blue-800 border-blue-200",
      Cancel: "bg-orange-100 text-orange-800 border-orange-200",
      "Not Eligible": "bg-pink-100 text-pink-800 border-pink-200",
      "SFE Exhausted": "bg-amber-100 text-amber-800 border-amber-200",
      Alumni: "bg-gray-200 text-gray-800 border-gray-300",
    };
    return colors[status] || "bg-gray-100 text-gray-600 border-gray-200";
  };

  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const currentStudents = filteredStudents.slice(startIdx, startIdx + rowsPerPage);

  const toggleMenu = (id) => setActionMenu(prev => (prev === id ? null : id));

  const getPageNumbers = (total, current, max = 7) => {
    const half = Math.floor(max / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    setActionMenu(null);
  };

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <div className="text-sm text-gray-600">Loading students…</div>
        </div>
      </div>
    );
  }

  if (editingStudentId) {
    return (
      <div className="w-full mt-12 bg-white rounded-xl shadow-lg">
        <EditStudent
          studentId={editingStudentId}
          onClose={() => setEditingStudentId(null)}
          onSave={(updated) => {
            setStudents(prev => prev.map(s => (s.id === updated.id ? updated : s)));
            setEditingStudentId(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 relative">
      
      {/* Export Modal */}
      <AnimatePresence>
        {showExportModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg p-6 shadow-xl w-80 max-w-full mx-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Export to Excel
                </h2>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-gray-600 text-sm mb-5">
                Choose which students to include in the export.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => exportToExcel(true)}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors group"
                >
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-green-700">
                      All Students
                    </div>
                    <div className="text-xs text-gray-500">
                      Export all {students.length.toLocaleString()} records
                    </div>
                  </div>
                  <Download size={16} className="text-gray-400 group-hover:text-green-600" />
                </button>
                <button
                  onClick={() => exportToExcel(false)}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors group"
                >
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                      Filtered Students
                    </div>
                    <div className="text-xs text-gray-500">
                      Export {filteredStudents.length.toLocaleString()} matching records
                    </div>
                  </div>
                  <Download size={16} className="text-gray-400 group-hover:text-blue-600" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg p-6 shadow-xl w-80 max-w-full mx-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Confirm Deletion
                </h2>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-gray-700 mb-6 text-sm">
                Are you sure you want to delete this student? This action cannot
                be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-sm rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-medium"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header Section */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">
              My Student Management
            </h1>
            <p className="text-gray-600 text-sm">
              Browse and manage your assigned student records
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-lg font-bold text-blue-600">
                {students.length.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total Students</div>
            </div>
            <motion.button
              onClick={() => setShowExportModal(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-sm transition-colors font-medium text-sm"
            >
              <Download size={16} />
              Export Excel
            </motion.button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search students by name, email, or ID..."
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        <div className="relative">
          <Filter
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={16}
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
          >
            <option value="all">All Statuses</option>
            {statusOptions
              .filter((opt) => opt !== "all")
              .map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
        <div className="text-gray-600 text-sm">
          Showing {startIdx + 1}-
          {Math.min(startIdx + rowsPerPage, filteredStudents.length)} of{" "}
          {filteredStudents.length.toLocaleString()} students
        </div>
        <div className="flex items-center gap-3">
          <label
            htmlFor="rowsPerPage"
            className="text-gray-700 text-sm font-medium"
          >
            Rows per page:
          </label>
          <select
            id="rowsPerPage"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-xs">
        <table className="w-full text-sm">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
            <tr>
              {[
                { label: "#", width: "w-12" },
                { label: "Student ID", width: "w-28" },
                { label: "Full Name", width: "w-40" },
                { label: "Email", width: "w-48" },
                { label: "Status", width: "w-36" },
                { label: "Created Date", width: "w-28" },
                { label: "Actions", width: "w-16" },
              ].map(({ label, width }) => (
                <th
                  key={label}
                  className={`px-4 py-3 text-left font-semibold text-gray-900 text-xs ${width}`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentStudents.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8">
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    <User size={32} className="mb-2 text-gray-300" />
                    <div className="text-sm font-medium mb-1">
                      No students found
                    </div>
                    <div className="text-xs text-gray-400">
                      Try adjusting your search or filters
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              currentStudents.map((s, i) => (
                <motion.tr
                  key={s.id}
                  className="hover:bg-gray-50 transition-colors group"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, delay: i * 0.01 }}
                >
                  <td className="px-4 py-3 text-center text-gray-500 font-medium text-xs">
                    {startIdx + i + 1}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        setEditingStudentId(s.id);
                        localStorage.setItem("student_id", s.id);
                      }}
                      className="text-blue-600 hover:text-blue-800 font-semibold text-left hover:underline transition-colors text-xs"
                    >
                      {s.studentId}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900 text-sm">
                      {s.name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-gray-700 text-xs">
                      <Mail
                        size={12}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span className="truncate">{s.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                        s.status,
                      )}`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Calendar
                        size={12}
                        className="text-gray-400 flex-shrink-0"
                      />
                      {s.createdDate}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center relative">
                    <button
                      className="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-blue-50"
                      onClick={() => toggleMenu(s.id)}
                    >
                      <Settings size={16} />
                    </button>
                    <AnimatePresence>
                      {actionMenu === s.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -5 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -5 }}
                          className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden"
                        >
                          <button
                            onClick={() => {
                              setEditingStudentId(s.id);
                              localStorage.setItem("student_id", s.id);
                              setActionMenu(null);
                            }}
                            className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-blue-50 w-full text-left transition-colors border-b border-gray-100 text-sm"
                          >
                            <Edit size={14} />
                            Edit Student
                          </button>
                          <button
                            className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 w-full text-left transition-colors text-sm"
                            onClick={() => {
                              setStudentToDelete(s.id);
                              setShowConfirm(true);
                              setActionMenu(null);
                            }}
                          >
                            <Delete size={14} />
                            Delete Student
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-6 pt-4 border-t border-gray-200">
          <div className="text-gray-600 text-xs">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-center">
            <button
              className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {getPageNumbers(totalPages, currentPage).map((num) => (
              <button
                key={num}
                onClick={() => goToPage(num)}
                className={`px-3 py-1.5 rounded font-medium transition-colors text-xs ${
                  currentPage === num
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                {num}
              </button>
            ))}

            <button
              className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
